import { type FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { decodeV8Token, isTokenExpired, saveAuthToken } from '@/auth/auth-token';
import { AUTH_CHANGE_EVENT, useAdminAuth } from '@/auth/use-admin-auth';

export default function LoginPage() {
  const { account } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (account) {
      const from =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    }
  }, [account, navigate, location]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = token.trim();
    if (!trimmed) {
      setError('토큰을 입력해주세요.');
      return;
    }
    const decoded = decodeV8Token(trimmed);
    if (!decoded) {
      setError('유효하지 않은 토큰입니다 (JWT 디코드 실패 또는 walletAddress 누락).');
      return;
    }
    if (isTokenExpired(decoded)) {
      setError('토큰이 만료되었습니다. 새 토큰을 가져와주세요.');
      return;
    }
    saveAuthToken(trimmed);
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle className="text-2xl text-primary">RB Admin Console</CardTitle>
          <CardDescription>V8 access token 을 붙여넣어 로그인합니다.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="rounded-md border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
              <p className="mb-2 font-semibold text-foreground">토큰 가져오는 방법 (Chrome 기준)</p>
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  아무 V8 사이트 (예:&nbsp;
                  <a
                    href="https://verse8.io"
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-primary underline-offset-2 hover:underline"
                  >
                    https://verse8.io
                  </a>
                  ) 에 로그인합니다.
                </li>
                <li>
                  데브콘솔 열기 —{' '}
                  <kbd className="rounded border bg-background px-1 font-mono">F12</kbd>
                  &nbsp;또는&nbsp;
                  <kbd className="rounded border bg-background px-1 font-mono">⌥⌘I</kbd>
                  &nbsp;(mac) /&nbsp;
                  <kbd className="rounded border bg-background px-1 font-mono">Ctrl+Shift+I</kbd>
                  &nbsp;(win)
                </li>
                <li>
                  상단 탭에서 <strong className="text-foreground">Application</strong>{' '}
                  (어플리케이션) 으로 이동.
                </li>
                <li>
                  좌측 사이드바에서{' '}
                  <strong className="text-foreground">Storage → Cookies → https://verse8.io</strong>{' '}
                  선택.
                </li>
                <li>
                  쿠키 목록에서 <code className="font-mono text-foreground">verse8_auth_token</code>{' '}
                  로 되어 있는 key 찾기.
                </li>
                <li>
                  <strong className="text-foreground">Value</strong> 칸에 들어있는 토큰 전체를 복사
                  — <code className="font-mono">eyJ…</code> 로 시작하는 긴 문자열입니다.
                </li>
                <li>
                  아래 textarea 에 붙여넣고{' '}
                  <strong className="text-foreground">Save &amp; Sign in</strong> 클릭.
                </li>
              </ol>
              <p className="mt-2 text-muted-foreground/80">
                토큰 만료(보통 7일) 시 자동으로 로그인 화면으로 돌아옵니다. 위 절차를 한 번 더
                반복하세요.
              </p>
            </div>
            <Textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              rows={6}
              spellCheck={false}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
              className="font-mono text-xs"
            />
            {error && (
              <Alert variant="destructive">
                <AlertTitle>로그인 실패</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" size="lg">
              Save &amp; Sign in
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
